import logger from '../utils/logger';
import apiService from '../apiService';

export interface BasePoints {
  'multiple-choice': number;
  'true-false': number;
  'matching': number;
  'fill-blanks': number;
  'ranking': number;
  'calculation': number;
  'short-answer': number;
}

export interface ScoringConfig {
  id: string;
  name: string;
  description?: string;
  basePoints: BasePoints;
  timeBonus: {
    enabled: boolean;
    maxBonusPercent: number;
    timeThresholdSeconds: number;
  };
  difficultyMultipliers: {
    easy: number;
    medium: number;
    hard: number;
  };
  streakBonus: {
    enabled: boolean;
    thresholdCount: number;
    bonusPoints: number;
  };
  penaltyForWrong: {
    enabled: boolean;
    pointsDeducted: number;
  };
  partialCredit: {
    enabled: boolean;
    percentages: {
      matching: number;
      fillBlanks: number;
      ranking: number;
    };
  };
  modeSpecific: {
    practice: {
      hintsEnabled: boolean;
      hintPenaltyPercent: number;
      explanationsShown: boolean;
    };
    exam: {
      strictTiming: boolean;
      noBackNavigation: boolean;
      randomizeQuestions: boolean;
    };
  };
  isActive: boolean;
  isDefault: boolean;
}

export interface QuestionScore {
  basePoints: number;
  difficultyBonus: number;
  timeBonus: number;
  streakBonus: number;
  penalty: number;
  partialCredit: number;
  totalPoints: number;
  breakdown: string[];
  band?: number; // Band score for short-answer questions (0-5)
}

export interface QuizScore {
  totalScore: number;
  maxPossibleScore: number;
  percentage: number;
  correctAnswers: number;
  partiallyCorrectAnswers?: number; // Questions with partial credit
  totalQuestions: number;
  streakCount: number;
  longestStreak: number;
  averageTimePerQuestion: number;
  timeSpent: number; // Total time spent on quiz in seconds
  questionScores: QuestionScore[];
  achievements: string[];
  streakBonus?: number; // Optional bonus for streaks
  perfectBonus?: number; // Optional bonus for perfect score
}

class ScoringEngine {
  private config: ScoringConfig | null = null;
  private currentStreak: number = 0;
  private longestStreak: number = 0;
  private questionScores: QuestionScore[] = [];

  async loadConfig(configId?: string): Promise<void> {
    try {
      const endpoint = configId
        ? `/scoring/admin/scoring-configs/${configId}`
        : '/scoring/admin/scoring-configs/default';

      const response = await apiService.get(endpoint);
      this.config = (response as any).data || response;
      logger.info('Loaded scoring config', { config: this.config });
    } catch (error) {
      logger.error('Failed to load scoring config', error);
      // Use default config as fallback
      this.config = this.getDefaultConfig();
    }
  }

  private getDefaultConfig(): ScoringConfig {
    return {
      id: 'default',
      name: 'Standard Scoring',
      description: 'Default scoring configuration',
      basePoints: {
        'multiple-choice': 5,
        'true-false': 3,
        'matching': 10,
        'fill-blanks': 8,
        'ranking': 8,
        'calculation': 10,
        'short-answer': 12
      },
      timeBonus: {
        enabled: true,
        maxBonusPercent: 20,
        timeThresholdSeconds: 30
      },
      difficultyMultipliers: {
        easy: 1.0,
        medium: 1.5,
        hard: 2.0
      },
      streakBonus: {
        enabled: true,
        thresholdCount: 3,
        bonusPoints: 5
      },
      penaltyForWrong: {
        enabled: false,
        pointsDeducted: 0
      },
      partialCredit: {
        enabled: true,
        percentages: {
          matching: 50,
          fillBlanks: 50,
          ranking: 33
        }
      },
      modeSpecific: {
        practice: {
          hintsEnabled: true,
          hintPenaltyPercent: 10,
          explanationsShown: true
        },
        exam: {
          strictTiming: true,
          noBackNavigation: false,
          randomizeQuestions: true
        }
      },
      isActive: true,
      isDefault: true
    };
  }

  calculateQuestionScore(
    isCorrect: boolean,
    difficulty: string = 'medium',
    timeSpent: number = 0,
    questionType: string = 'multiple-choice',
    partialCorrectCount?: number,
    totalParts?: number,
    hintUsed: boolean = false,
    mode: 'practice' | 'exam' = 'practice',
    bandScore?: number // AI evaluation band for short-answer questions (0-4)
  ): QuestionScore {
    if (!this.config) {
      this.config = this.getDefaultConfig();
    }

    const score: QuestionScore = {
      basePoints: 0,
      difficultyBonus: 0,
      timeBonus: 0,
      streakBonus: 0,
      penalty: 0,
      partialCredit: 0,
      totalPoints: 0,
      breakdown: []
    };

    // Get base points for this question type
    const questionTypeKey = questionType as keyof BasePoints;
    const basePointsForType = this.config.basePoints[questionTypeKey] || this.config.basePoints['multiple-choice'];

    // Get difficulty multiplier for calculations
    const difficultyMultiplier = this.config.difficultyMultipliers[difficulty as keyof typeof this.config.difficultyMultipliers] || 1;

    // Handle band-based scoring for short-answer questions
    if (questionType === 'short-answer' && bandScore !== undefined && bandScore !== null) {
      // Store the band score for UI display
      score.band = bandScore;

      // Award proportional points based on band (0-4 or 0-5)
      // Band 0 = 0%, Band 1 = 25%, Band 2 = 50%, Band 3 = 75%, Band 4 = 100%, Band 5 = 100%
      const maxBand = bandScore > 4 ? 5 : 4;
      const bandPercentage = Math.min(bandScore / maxBand, 1);
      score.basePoints = Math.round(basePointsForType * bandPercentage);
      score.breakdown.push(`Band ${bandScore} score: ${score.basePoints} points (${Math.round(bandPercentage * 100)}%)`);

      // Apply difficulty bonus
      score.difficultyBonus = Math.round((score.basePoints * difficultyMultiplier) - score.basePoints);
      if (score.difficultyBonus > 0) {
        score.breakdown.push(`Difficulty bonus (${difficulty}): +${score.difficultyBonus}`);
      }

      // Update streak only for Band 4 (perfect score)
      if (bandScore === 4) {
        this.currentStreak++;
        if (this.currentStreak > this.longestStreak) {
          this.longestStreak = this.currentStreak;
        }

        if (this.config.streakBonus.enabled && this.currentStreak >= this.config.streakBonus.thresholdCount) {
          score.streakBonus = this.config.streakBonus.bonusPoints;
          score.breakdown.push(`Streak bonus (${this.currentStreak} correct): +${score.streakBonus}`);
        }
      } else {
        // Reset streak for bands below 4
        this.currentStreak = 0;
      }
    } else if (isCorrect) {
      // Base points (only for correct answers)
      score.basePoints = basePointsForType;
      score.breakdown.push(`Base points: ${score.basePoints}`);

      // Difficulty bonus (only for correct answers)
      score.difficultyBonus = Math.round((score.basePoints * difficultyMultiplier) - score.basePoints);
      if (score.difficultyBonus > 0) {
        score.breakdown.push(`Difficulty bonus (${difficulty}): +${score.difficultyBonus}`);
      }
      // Time bonus (exam mode only - no time bonus in practice/warmup)
      if (mode === 'exam' && this.config.timeBonus.enabled && timeSpent <= this.config.timeBonus.timeThresholdSeconds) {
        const timeBonusPercent = Math.min(
          this.config.timeBonus.maxBonusPercent,
          Math.round(((this.config.timeBonus.timeThresholdSeconds - timeSpent) / this.config.timeBonus.timeThresholdSeconds) * this.config.timeBonus.maxBonusPercent)
        );
        score.timeBonus = Math.round((score.basePoints + score.difficultyBonus) * (timeBonusPercent / 100));
        if (score.timeBonus > 0) {
          score.breakdown.push(`Time bonus (${timeSpent}s): +${score.timeBonus}`);
        }
      }

      // Streak bonus
      this.currentStreak++;
      if (this.currentStreak > this.longestStreak) {
        this.longestStreak = this.currentStreak;
      }

      if (this.config.streakBonus.enabled && this.currentStreak >= this.config.streakBonus.thresholdCount) {
        score.streakBonus = this.config.streakBonus.bonusPoints;
        score.breakdown.push(`Streak bonus (${this.currentStreak} correct): +${score.streakBonus}`);
      }

      // Hint penalty (practice mode only)
      if (mode === 'practice' && hintUsed && this.config.modeSpecific.practice.hintsEnabled) {
        const hintPenalty = Math.round((score.basePoints + score.difficultyBonus) * (this.config.modeSpecific.practice.hintPenaltyPercent / 100));
        score.penalty = -hintPenalty;
        score.breakdown.push(`Hint used: ${score.penalty}`);
      }
    } else {
      // Reset streak
      this.currentStreak = 0;

      // Partial credit for certain question types
      if (this.config.partialCredit.enabled && partialCorrectCount !== undefined && totalParts !== undefined && totalParts > 0) {
        const partialPercentage = partialCorrectCount / totalParts;
        let creditPercent = 0;

        switch (questionType) {
          case 'matching':
            creditPercent = this.config.partialCredit.percentages.matching;
            break;
          case 'fill-blanks':
            creditPercent = this.config.partialCredit.percentages.fillBlanks;
            break;
          case 'ranking':
            creditPercent = this.config.partialCredit.percentages.ranking;
            break;
        }

        if (creditPercent > 0) {
          // Calculate partial credit based on question-type-specific base points with difficulty multiplier
          const basePointsWithDifficulty = basePointsForType * difficultyMultiplier;
          score.partialCredit = Math.round(basePointsWithDifficulty * partialPercentage * (creditPercent / 100));
          if (score.partialCredit > 0) {
            score.breakdown.push(`Partial credit (${partialCorrectCount}/${totalParts} correct): +${score.partialCredit}`);
          }
        }
      }

      // Wrong answer penalty
      if (this.config.penaltyForWrong.enabled && this.config.penaltyForWrong.pointsDeducted > 0) {
        score.penalty = -this.config.penaltyForWrong.pointsDeducted;
        score.breakdown.push(`Wrong answer penalty: ${score.penalty}`);
      }
    }

    // Calculate total
    score.totalPoints = Math.max(0,
      score.basePoints +
      score.difficultyBonus +
      score.timeBonus +
      score.streakBonus +
      score.penalty +
      score.partialCredit
    );

    this.questionScores.push(score);
    return score;
  }

  calculateQuizScore(
    correctAnswers: number,
    totalQuestions: number,
    averageTimePerQuestion: number,
    timeSpent: number = 0
  ): QuizScore {
    const totalScore = this.questionScores.reduce((sum, score) => sum + score.totalPoints, 0);
    const maxPossibleScore = this.calculateMaxPossibleScore(totalQuestions);

    const quizScore: QuizScore = {
      totalScore,
      maxPossibleScore,
      percentage: Math.round((totalScore / maxPossibleScore) * 100),
      correctAnswers,
      totalQuestions,
      streakCount: this.currentStreak,
      longestStreak: this.longestStreak,
      averageTimePerQuestion,
      timeSpent,
      questionScores: this.questionScores,
      achievements: this.checkAchievements(correctAnswers, totalQuestions, this.longestStreak)
    };

    return quizScore;
  }

  private calculateMaxPossibleScore(totalQuestions: number): number {
    if (!this.config) {
      this.config = this.getDefaultConfig();
    }

    // Calculate average base points across all question types
    const basePointsValues = Object.values(this.config.basePoints);
    const avgBasePoints = basePointsValues.reduce((sum, val) => sum + val, 0) / basePointsValues.length;

    // Calculate max score with all bonuses using average base points
    const baseScore = avgBasePoints * totalQuestions;
    const maxDifficultyBonus = baseScore * (this.config.difficultyMultipliers.hard - 1);
    const maxTimeBonus = (baseScore + maxDifficultyBonus) * (this.config.timeBonus.maxBonusPercent / 100);
    const maxStreakBonus = this.config.streakBonus.bonusPoints * (totalQuestions - this.config.streakBonus.thresholdCount + 1);

    return Math.round(baseScore + maxDifficultyBonus + maxTimeBonus + maxStreakBonus);
  }

  private checkAchievements(correct: number, total: number, streak: number): string[] {
    const achievements: string[] = [];
    const percentage = (correct / total) * 100;

    if (percentage === 100) {
      achievements.push('Perfect Score');
    } else if (percentage >= 90) {
      achievements.push('Excellence');
    } else if (percentage >= 80) {
      achievements.push('Great Job');
    }

    if (streak >= 10) {
      achievements.push('Unstoppable Streak');
    } else if (streak >= 5) {
      achievements.push('On Fire');
    }

    if (this.questionScores.every(s => s.timeBonus > 0)) {
      achievements.push('Speed Demon');
    }

    return achievements;
  }

  reset(): void {
    this.currentStreak = 0;
    this.longestStreak = 0;
    this.questionScores = [];
  }

  getConfig(): ScoringConfig | null {
    return this.config;
  }

  getCurrentStreak(): number {
    return this.currentStreak;
  }

  getLongestStreak(): number {
    return this.longestStreak;
  }

  getQuestionScores(): QuestionScore[] {
    return this.questionScores;
  }

  // Real-time score calculation for display
  getRunningScore(): number {
    return this.questionScores.reduce((sum, score) => sum + score.totalPoints, 0);
  }

  // Predict final score based on current performance
  predictFinalScore(answeredCount: number, totalQuestions: number): number {
    if (answeredCount === 0) return 0;

    const currentAverage = this.getRunningScore() / answeredCount;
    const remainingQuestions = totalQuestions - answeredCount;
    const predictedRemainingScore = currentAverage * remainingQuestions;

    return Math.round(this.getRunningScore() + predictedRemainingScore);
  }
}

export default new ScoringEngine();